import { useCallback } from "react"
import { batch } from "@preact/signals-react"
import { postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { useToast } from "@hooks"
import {
    brandForm, catalogueDetailsFormIsLoading, catalogueDetailsFormRowData, catalogueDetailsFormRowDataMultiple, catalogueDetailsFormTitle, catalogueDetailsTableData, siteForm,
    skuForm
} from "../@signals"


export default function useCatalogueDetailsForm(dataRow={}) {
	const { showToast } = useToast()

	const handleOpenForm = useCallback(() => batch(() => {
		catalogueDetailsFormTitle.value = dataRow.productTitle || dataRow.skuName
		brandForm.value = dataRow.brandID ?? null
		skuForm.value = dataRow.skuID ?? null
		siteForm.value = { siteID: dataRow.siteID, siteCountry: dataRow.country }
		catalogueDetailsFormRowData.value = dataRow
	}), [JSON.stringify(dataRow)])

	const handleOpenFormMultiple = useCallback((selectedRows) => batch(() => {
        if (selectedRows.length < 10){
            catalogueDetailsFormTitle.value = `Multiple Edit (${selectedRows.length} selected)`
            catalogueDetailsFormRowDataMultiple.value = selectedRows
            const firstRow = selectedRows[0]
            brandForm.value = firstRow.brandID ?? null
            skuForm.value = firstRow.skuID ?? null
            siteForm.value = { siteID: firstRow.siteID, siteCountry: firstRow.country }
            catalogueDetailsFormRowData.value = firstRow
        }else {
            showToast.warning('Only allowed to edit a maximum of 10 rows at a time.')
        }
		
    }), [JSON.stringify(dataRow)])

	const handleSendToBack = useCallback((extraData = {}, callback = {}) => {
        const isMultiple = catalogueDetailsFormRowDataMultiple.value.length > 0
        const apiUrl = isMultiple
            ? `${BACKEND_APIS.ADMIN}/product-review-new/multiple-update-mapping`
            : `${BACKEND_APIS.ADMIN}/product-review-new/update-mapping`

        const payload = isMultiple
            ? {
                items: catalogueDetailsFormRowDataMultiple.value.map(item => ({
                    item: { ...item },
                    mapping: { ...item, ...extraData, validated: true }
                }))
            }
            : {
                item: { ...dataRow, ...catalogueDetailsFormRowData.value },
                mapping: { ...dataRow, ...catalogueDetailsFormRowData.value, ...extraData, validated: true }
            }

        showToast.promise(postSubmit(apiUrl, payload), callback)
    }, [JSON.stringify(dataRow)])
	

	const handleUpdate = useCallback(() => {
        const isMultiple = catalogueDetailsFormRowDataMultiple.value.length > 0
        const ids = isMultiple 
            ? catalogueDetailsFormRowDataMultiple.value.map(row => row.id)
            : [catalogueDetailsFormRowData.value.id]

        catalogueDetailsFormIsLoading.value = true

        const isSkuEmpty = !skuForm.value || skuForm.value === ""
        const isBrandEmpty = !brandForm.value || brandForm.value === ""

        const extraData = {
            brandID: isSkuEmpty && isBrandEmpty ? null : brandForm.value,
            skuID: isSkuEmpty ? null : skuForm.value,
            qa: true,
            validated: true,
            skip: isSkuEmpty,
            resetBrand: isSkuEmpty && isBrandEmpty
        }

        handleSendToBack(extraData, {
            onSuccess: (updatedRows) => {
                if (isMultiple) {
                    catalogueDetailsTableData.value = catalogueDetailsTableData.value.map(row => 
                        ids.includes(row.id) ? updatedRows.find(updatedRow => updatedRow.id === row.id) || row : row
                    )
					return `Loaded ${updatedRows.length} of ${ids.length} items successfully.`
                } else {
                    if (updatedRows && !!updatedRows.skuID) {
                        updatedRows.isMapped = true
                    }else{
                        updatedRows.isMapped = false
                    }
                    catalogueDetailsTableData.value = catalogueDetailsTableData.value.map(row =>
                        row.id === ids[0] ? updatedRows : row
                    )
                }
                return 'Items loaded successfully.'
            },
            onFinish: () => catalogueDetailsFormIsLoading.value = false
        })
    }, [JSON.stringify(dataRow)])

	return {
		isOpenEditDrawer: catalogueDetailsFormRowData.value.id === dataRow.id,
		isLoadingDrawer: catalogueDetailsFormIsLoading.value === dataRow.id,

		handleOpenForm,
		handleOpenFormMultiple,
        handleSendToBack,
        handleUpdate
	}
}